import { debugLog } from '../core/utils/logger';
import { SigningAdapter, type SignL1ActionParams } from './base-adapter';
import { EIP712_DOMAINS, EIP712_TYPES } from '../core/constants';
import type { EthereumProvider } from '../types/privy';

export interface PrivyProviderAdapterConfig {
  getProvider: () => Promise<EthereumProvider>;
  walletAddress: string;
  showWalletUIs?: boolean;
}

/**
 * Signing adapter that uses eth_signTypedData_v4 directly through Privy's provider
 * This bypasses Privy's signTypedData hook and calls the RPC method directly
 */
export class PrivyProviderAdapter extends SigningAdapter {
  private config: PrivyProviderAdapterConfig;

  constructor(config: PrivyProviderAdapterConfig) {
    super();
    this.config = config;
  }

  async signL1Action(params: SignL1ActionParams): Promise<`0x${string}`> {
    const eip712Message = {
      domain: EIP712_DOMAINS.EXCHANGE,
      primaryType: 'Agent',
      types: {
        EIP712Domain: [
          { name: 'name', type: 'string' },
          { name: 'version', type: 'string' },
          { name: 'chainId', type: 'uint256' },
          { name: 'verifyingContract', type: 'address' },
        ],
        ...EIP712_TYPES.AGENT,
      },
      message: {
        source: params.isTestnet ? 'b' : 'a',
        connectionId: params.actionHash,
      },
    };

    debugLog('[Privy Provider Adapter] Signing EIP-712 message:', JSON.stringify(eip712Message, null, 2));
    debugLog('[Privy Provider Adapter] Wallet address:', this.config.walletAddress);

    // Get provider and call eth_signTypedData_v4 directly (like direct SDK does)
    const provider = await this.config.getProvider();

    // eth_signTypedData_v4 expects [address, stringified typed data]
    // Keep original address case (checksum format)
    debugLog('[Privy Provider Adapter] Address being used for signing:', this.config.walletAddress);
    const signature = await provider.request({
      method: 'eth_signTypedData_v4',
      params: [this.config.walletAddress, JSON.stringify(eip712Message)],
    });

    debugLog('[Privy Provider Adapter] Received signature:', signature);

    return signature as `0x${string}`;
  }

  async isReady(): Promise<boolean> {
    return !!this.config.getProvider && !!this.config.walletAddress;
  }

  getWalletAddress(): string {
    return this.config.walletAddress;
  }
}
